import { DesignResource, DesignValidationResultResource, ValidationValueResource } from '@/api/serverApi'
import { BaseAccordion, LeadingModeOfFailureIcon, LoadingSpinner } from '@cfs-fixings/shared'
import { useEffect, useMemo } from 'react'
import UtilisationPercentagePill from './components/UtilisationPercentagePill'
import PanelSections from '../properties/components/PanelSections'
import { useDispatch, useSelector } from 'react-redux'
import { validateDesign } from '@/stores/design-validation'
import { AppDispatch, RootState } from '@/stores'
import PanelSection from '../properties/components/PanelSection'

type PropTypes = {
  design: DesignResource
}

const utilisationMapping = {
  tension: [
    {
      key: 'tension_anchor_failure',
      label: 'Anchor Evaluation',
    },
    {
      key: 'tension_anchor_channel_connection_failure',
      label: 'Anchor Channel Connection Evaluation',
    },
    {
      key: 'tension_local_flexure_of_channel_lips_failure',
      label: 'Local Flexure of Channel Lips Evaluation',
    },
    {
      key: 'tension_channel_bolt_failure',
      label: 'Channel Bolt Evaluation',
    },
    {
      key: 'tension_flexure_of_channel_failure',
      label: 'Flexure of Channel Evaluation',
    },
    {
      key: 'tension_pull_out_failure',
      label: 'Pull Out Evaluation',
    },
    {
      key: 'tension_splitting_failure',
      label: 'Concrete Splitting Evaluation',
    },
    {
      key: 'tension_blowout_failure',
      label: 'Concrete Blowout Evaluation',
    },
  ],
  shear: [
    {
      key: 'shear_channel_bolt_failure',
      label: 'Channel Bolt Evaluation',
    },
    {
      key: 'shear_anchor_failure',
      label: 'Anchor Evaluation',
    },
    {
      key: 'shear_anchor_channel_connection_failure',
      label: 'Anchor Channel Connection Evaluation',
    },
    {
      key: 'shear_local_flexure_of_channel_lips_failure',
      label: 'Local Flexure of Channel Lips Evaluation',
    },
    {
      key: 'shear_pry_out_failure',
      label: 'Pry-Out Evaluation',
    },
    {
      key: 'shear_concrete_edge_failure',
      label: 'Concrete Edge Evaluation',
    },
  ],
  combined: [
    {
      key: 'combined_t_bolt_failure',
      label: 'Combined Bolt Evaluation',
    },
    {
      key: 'combined_channel_lips_and_flexural_failure_of_channel',
      label: 'Channel Lips and Flexure of Channel Evaluation',
    },
    {
      key: 'combined_anchor_and_connection_between_anchor_and_channel_failure',
      label: 'Anchor and Connection Between Anchor and Channel Evaluation',
    },
    {
      key: 'combined_concrete_failure',
      label: 'Concrete Evaluation',
    },
  ],
}

const getUtilisationValue = (forceValues: { key: string, label: string }[], validationResponses: DesignValidationResultResource) => {
  return forceValues.map((item) => {
    const record = validationResponses.validation_to_display.find((dataItem) => dataItem.id === item.key)
    return record
      ? { label: item.label, record: record }
      : undefined
  }).filter((item) => item)
}

type UtilisationOverviewSectionPropTypes = {
  key?: string,
  label: string,
  utilisation?: string,
  error?: string,
  message?: string,
  modeOfFailure?: string,
}

const UtilisationOverviewSection = ({ label, utilisation, error, message, modeOfFailure }: UtilisationOverviewSectionPropTypes) => {
  return (
    <div className="flex flex-col">
      <div className="flex flex-row items-center justify-between w-full">
        <div className="text-xs pr-3">{label}</div>
        {
          utilisation
            ? <UtilisationPercentagePill percentage={parseFloat(utilisation) * 100} />
            : <p className="text-[10px] max-w-24">{error ?? message}</p> // This will be a message describing that no calculation is needed
        }
      </div>
      {
        modeOfFailure
          && (
            <>
              <div className="flex flex-row gap-1 pt-[19px]">
                <LeadingModeOfFailureIcon className="w-4 h-4" />
                <p className="text-xs leading-[18px] text-gray-600">Leading mode of failure:</p>
              </div>
              <p className="text-xs pl-5 pt-1 font-semibold leading-[18px] text-gray-900">{modeOfFailure}</p>
            </>
          )
      }
    </div>
  )
}

const UtilisationOverview = ({ design }: PropTypes) => {
  const dispatch = useDispatch<AppDispatch>()

  const validationResponses = useSelector((state: RootState) => state.designValidation.validationResponses)
  const isValidating = useSelector((state: RootState) => state.designValidation.validating)

  useEffect(() => {
    dispatch(validateDesign({ design: design.id }))
  }, [design.id, dispatch])

  const tensionUtilisationValue = useMemo(() =>
    validationResponses ? getUtilisationValue(utilisationMapping.tension, validationResponses) : [],
  [validationResponses])

  const shearUtilisationValue = useMemo(() =>
    validationResponses ? getUtilisationValue(utilisationMapping.shear, validationResponses) : [],
  [validationResponses])

  const combinedUtilisationValue = useMemo(() =>
    validationResponses ? getUtilisationValue(utilisationMapping.combined, validationResponses) : [],
  [validationResponses])

  const getMaxUtilisationPercentage = (utilisationValues: Array<{ record: { utilisation: string } }>) => {
    if (utilisationValues.length !== 0) {
      return Math.max(
        ...utilisationValues.map((item) => {
          const utilisation = parseFloat(item.record.utilisation)
          return isNaN(utilisation) ? 0 : utilisation * 100
        }),
      )
    }
    else {
      return 0
    }
  }

  const maxTensionPercentage = useMemo(() => {
    const filteredValues = tensionUtilisationValue.filter((item): item is { label: string; record: ValidationValueResource } => item !== undefined && item.record.utilisation !== undefined)
    return getMaxUtilisationPercentage(filteredValues)
  }, [tensionUtilisationValue])

  const maxShearPercentage = useMemo(() => {
    const filteredValues = shearUtilisationValue.filter((item): item is { label: string; record: ValidationValueResource } => item !== undefined && item.record.utilisation !== undefined)
    return getMaxUtilisationPercentage(filteredValues)
  }, [shearUtilisationValue])

  const maxCombinedPercentage = useMemo(() => {
    const filteredValues = combinedUtilisationValue.filter((item): item is { label: string; record: ValidationValueResource } => item !== undefined && item.record.utilisation !== undefined)
    return getMaxUtilisationPercentage(filteredValues)
  }, [combinedUtilisationValue])

  // Get utilisation items for tension by the key in the response, then map them to a label with the utilisation
  const getTensionUtilisationItems = useMemo(() => tensionUtilisationValue.map((item) => {
    if (!item) {
      return undefined
    }

    return (
      <div key={item.record.id} className="flex flex-row items-center justify-between w-full">
        <div className="text-xs pr-3">{item.label}</div>
        {
          item.record.utilisation
            ? <UtilisationPercentagePill percentage={parseFloat(item.record.utilisation) * 100} />
            : <p className="pl-1 text-[10px] max-w-24">{item.record.error ?? item.record.message}</p> // This will be a message describing that no calculation is needed
        }
      </div>
    )
  }).filter((item) => item), [tensionUtilisationValue])

  // Get utilisation items for shear by the key in the response, then map them to a label with the utilisation
  const getShearUtilisationItems = useMemo(() => shearUtilisationValue.map((item) => {
    if (!item) {
      return undefined
    }

    return (
      <div key={item.record.id} className="flex flex-row items-center justify-between w-full">
        <div className="text-xs pr-3">{item.label}</div>
        {
          item.record.utilisation
            ? <UtilisationPercentagePill percentage={parseFloat(item.record.utilisation) * 100} />
            : <p className="text-[10px] max-w-24">{item.record.error ?? item.record.message}</p> // This will be a message describing that no calculation is needed
        }
      </div>
    )
  }).filter((item) => item), [shearUtilisationValue])

  // Get utilisation items for shear by the key in the response, then map them to a label with the utilisation
  const getCombinedUtilisationItems = useMemo(() => combinedUtilisationValue.map((item) => {
    if (!item) {
      return undefined
    }

    return (
      <div key={item.record.id} className="flex flex-row items-center justify-between w-full">
        <div className="text-xs pr-3">{item.label}</div>
        {
          item.record.utilisation
            ? <UtilisationPercentagePill percentage={parseFloat(item.record.utilisation) * 100} />
            : <p className="text-[10px] max-w-24">{item.record.error ?? item.record.message}</p> // This will be a message describing that no calculation is needed
        }
      </div>
    )
  }).filter((item) => item), [combinedUtilisationValue])

  const utilisationOverview = useMemo(() => {
    const sortedTensionUtilisation = [...tensionUtilisationValue].sort((a, b) => parseFloat(b!.record.utilisation) - parseFloat(a!.record.utilisation))
    const sortedShearUtilisation = [...shearUtilisationValue].sort((a, b) => parseFloat(b!.record.utilisation) - parseFloat(a!.record.utilisation))
    const sortedCombinedUtilisation = [...combinedUtilisationValue].sort((a, b) => parseFloat(b!.record.utilisation) - parseFloat(a!.record.utilisation))

    const maxTensionUtilisation = sortedTensionUtilisation[0]
    const maxShearUtilisation = sortedShearUtilisation[0]
    const maxCombinedUtilisation = sortedCombinedUtilisation[0]

    let utilisationItems = []

    if (maxTensionUtilisation) {
      utilisationItems.push((
        <UtilisationOverviewSection
          key="Tension Evaluation"
          label="Tension Evaluation"
          utilisation={maxTensionUtilisation.record.utilisation}
          error={maxTensionUtilisation.record.error}
          message={maxTensionUtilisation.record.message}
          modeOfFailure={maxTensionUtilisation.label}
        />
      ))
    }

    if (maxShearUtilisation) {
      utilisationItems.push((
        <UtilisationOverviewSection
          key="Shear Evaluation"
          label="Shear Evaluation"
          utilisation={maxShearUtilisation.record.utilisation}
          error={maxShearUtilisation.record.error}
          message={maxShearUtilisation.record.message}
          modeOfFailure={maxShearUtilisation.label}
        />
      ))
    }

    if (maxCombinedUtilisation) {
      utilisationItems.push((
        <UtilisationOverviewSection
          key="Combined Evaluation"
          label="Combined Evaluation"
          utilisation={maxCombinedUtilisation.record.utilisation}
          error={maxCombinedUtilisation.record.error}
          message={maxCombinedUtilisation.record.message}
          modeOfFailure={maxCombinedUtilisation.label}
        />
      ))
    }

    return utilisationItems
  }, [tensionUtilisationValue, shearUtilisationValue, combinedUtilisationValue])

  return (
    <PanelSections>
      {
        validationResponses?.validation_to_display.length === 0 && isValidating
        && (
          <div className="w-full flex items-center justify-center py-5">
            <LoadingSpinner className="h-4 w-4" />
          </div>
        )
      }
      {
        validationResponses?.validation_to_display.length === 0 && !isValidating
        && (
          <div className="w-full flex items-center justify-center py-5 px-4 text-wrap text-xs text-gray-700">
            Invalid configuration, please add at least a cast-in channel with two T-bolts.
          </div>
        )
      }
      {
        validationResponses?.validation_to_display.length !== 0
        && <>
          <PanelSection title="Overview">
            <div className="flex flex-col gap-[19px] pt-2">
              {utilisationOverview}
            </div>
          </PanelSection>
          <BaseAccordion title="Tension" collapsedContent={maxTensionPercentage > 0 ? <UtilisationPercentagePill percentage={maxTensionPercentage} /> : null}>
            <div className="flex flex-col gap-[19px] pt-2">
              {getTensionUtilisationItems}
            </div>
          </BaseAccordion>
          <BaseAccordion title="Shear" collapsedContent={maxShearPercentage > 0 ? <UtilisationPercentagePill percentage={maxShearPercentage} /> : null}>
            <div className="flex flex-col gap-[19px] pt-2">
              {getShearUtilisationItems}
            </div>
          </BaseAccordion>
          <BaseAccordion title="Combined" collapsedContent={maxCombinedPercentage > 0 ? <UtilisationPercentagePill percentage={maxCombinedPercentage} /> : null}>
            <div className="flex flex-col gap-[19px] pt-2">
              {getCombinedUtilisationItems}
            </div>
          </BaseAccordion>
        </>
      }
    </PanelSections>
  )
}

export default UtilisationOverview
